import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import PromoBanner from "@/components/PromoBanner";
import { trackEvent } from "@/utils/analytics";

const links = [
  { label: "Fonctionnalités", to: "/features", testid: "nav-link-features" },
  { label: "Pour qui ?", to: "/#pour-qui", testid: "nav-link-for-who" },
  { label: "Tarifs", to: "/pricing", testid: "nav-link-pricing" },
  { label: "FAQ", to: "/#faq", testid: "nav-link-faq" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <div data-testid="header-stack" className="fixed inset-x-0 top-0 z-50">
      <PromoBanner />
      <header data-testid="site-header" className="border-b border-slate-200/70 bg-white/85 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" data-testid="nav-logo" className="flex items-center" onClick={() => setOpen(false)}>
          <img src="/actipilote-logo.png" alt="ActiPilote" className="h-8 w-auto" />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="Navigation principale">
          {links.map((l) =>
            l.to.startsWith("/#") ? (
              <Link key={l.label} to={l.to} data-testid={l.testid} className="rounded-full px-4 py-2 text-sm font-semibold text-navy/70 transition-colors hover:bg-mist hover:text-navy">
                {l.label}
              </Link>
            ) : (
              <NavLink key={l.label} to={l.to} data-testid={l.testid}
                className={({ isActive }) =>
                  `rounded-full px-4 py-2 text-sm font-semibold transition-colors ${isActive ? "bg-mist text-navy" : "text-navy/70 hover:bg-mist hover:text-navy"}`
                }>
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a href="https://app.actipilote.com/login" data-testid="nav-login-button" className="rounded-full px-4 py-2 text-sm font-semibold text-navy/70 transition-colors hover:text-navy">
            Se connecter
          </a>
          <Link to="/contact" onClick={() => trackEvent("click_demo", { location: "header" })} data-testid="nav-cta-button" className="group inline-flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white shadow-glow transition-all duration-300 hover:-translate-y-0.5 hover:bg-brand-700">
            Demander une démo
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </div>

        <button data-testid="mobile-menu-toggle" className="rounded-lg p-2 text-navy lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-slate-200/70 bg-white lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="space-y-1 px-4 py-4">
              {links.map((l) => (
                <Link key={l.label} to={l.to} data-testid={`${l.testid}-mobile`} onClick={() => setOpen(false)}
                  className="block rounded-xl px-4 py-3 text-base font-semibold text-navy/80 hover:bg-mist">
                  {l.label}
                </Link>
              ))}
              <div className="flex flex-col gap-2 pt-3">
                <a href="https://app.actipilote.com/login" data-testid="nav-login-button-mobile" className="rounded-xl border border-slate-200 px-4 py-3 text-center text-sm font-bold text-navy">
                  Se connecter
                </a>
                <Link to="/contact" data-testid="nav-cta-button-mobile" onClick={() => { trackEvent("click_demo", { location: "header_mobile" }); setOpen(false); }} className="rounded-xl bg-brand px-4 py-3 text-center text-sm font-bold text-white">
                  Demander une démo
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </header>
    </div>
  );
};

export default Header;
